import React, { useState } from 'react';
import { Squares2X2Icon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination, useDebounce } from '../../hooks';
import { productsAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, StatusBadge,
  Pagination, LoadingPage, EmptyState,
} from '../../components/common/UI';


const StockEditor = ({ product, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [qty, setQty]         = useState(product.inventory?.stock_qty ?? product.stock_qty ?? 0);

  const { mutate: update, loading } = useMutation(
    () => productsAPI.updateInventory({ product_id: product.id, stock_qty: parseInt(qty) }),
    { 
      successMsg: 'Stock updated', 
      onSuccess: () => { 
        setEditing(false); 
        window.dispatchEvent(new CustomEvent('inventoryUpdated'));
        onSaved?.(); 
      } 
    }
  );

  const current = product.inventory?.stock_qty ?? product.stock_qty ?? 0;
  const isLow   = current <= (product.low_stock_alert || 5);

  if (editing) return (
    <div className="flex items-center gap-1.5">
      <input
        type="number" min={0} value={qty}
        onChange={e => setQty(e.target.value)}
        className="input w-20 text-center text-sm py-1"
        autoFocus
      />
      <button
        onClick={() => update()} disabled={loading}
        className="btn btn-ghost p-1.5 rounded-lg text-success-600 hover:bg-success-50"
      >
        {loading
          ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-success-500 border-t-transparent" />
          : <CheckIcon className="h-4 w-4" />
        }
      </button>
      <button
        onClick={() => { setQty(current); setEditing(false); }}
        className="btn btn-ghost p-1.5 rounded-lg text-danger-500 hover:bg-danger-50"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      <span className={`font-mono text-sm font-semibold ${isLow ? 'text-danger-600' : 'text-surface-700'}`}>
        {current}
        {isLow && <span className="ml-1 text-xs">⚠️</span>}
      </span>
      <button
        onClick={() => { setQty(current); setEditing(true); }}
        className="btn btn-ghost p-1 rounded-lg text-surface-400 hover:text-surface-700"
      >
        <PencilSquareIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default function InventoryPage({ business }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const { page, setPage, reset } = usePagination();
  const dSearch = useDebounce(search);

  const { data, loading, refetch } = useQuery(
    () => productsAPI.list({
      page,
      limit: 30,
      business_id: business?.id,
      search:   dSearch || undefined,
      in_stock: filter === 'in_stock' ? 1 : undefined,
    }),
    [page, dSearch, filter, business?.id],  
  );

  const products = data?.data?.rows || data?.rows || [];
  const total    = data?.data?.count || data?.count || 0;
  const pages    = Math.ceil(total / 30) || 1;

  const displayProducts = filter === 'low'
    ? products.filter(p => (p.inventory?.stock_qty ?? p.stock_qty ?? 0) <= (p.low_stock_alert || 5))
    : products;

  const lowStockCount = products.filter(
    p => (p.inventory?.stock_qty ?? p.stock_qty ?? 0) <= (p.low_stock_alert || 5)
  ).length;

  const outOfStockCount = products.filter(
    p => (p.inventory?.stock_qty ?? p.stock_qty ?? 0) === 0
  ).length;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader
        title="Inventory Management"
        subtitle={`${total} products — ${lowStockCount} low stock`}
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          ['Total Products', total,          'brand'],
          ['Low Stock',      lowStockCount,  'red'],
          ['Out of Stock',   outOfStockCount,'yellow'],
        ].map(([label, val, color]) => (
          <Card key={label} className="p-4">
            <p className={`font-display text-2xl font-bold
              ${color === 'red'    ? 'text-danger-600'  :
                color === 'yellow' ? 'text-warning-600' :
                'text-brand-700'}`}>
              {val}
            </p>
            <p className="text-xs text-surface-400 mt-0.5">{label}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput
            value={search}
            onChange={v => { setSearch(v); reset(); }}
            placeholder="Search products…"
            className="flex-1 min-w-[200px]"
          />
          <div className="flex gap-1 rounded-xl bg-surface-100 p-1">
            {[
              ['all',      'All'],
              ['in_stock', 'In Stock'],
              ['low',      'Low Stock'],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => { setFilter(v); reset(); }}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors
                  ${filter === v
                    ? 'bg-white text-surface-900 shadow-card'
                    : 'text-surface-500 hover:text-surface-700'}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {loading ? <LoadingPage /> : displayProducts.length === 0 ? (
          <EmptyState icon={Squares2X2Icon} title="No products found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Stock</th>
                  <th>Reserved</th>
                  <th>Sold</th>
                  <th>Alert At</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {displayProducts.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {p.primary_image?.url
                          ? <img
                              src={p.primary_image.url}
                              alt={p.name}
                              className="h-9 w-9 rounded-lg object-cover flex-shrink-0"
                            />
                          : <div className="h-9 w-9 rounded-lg bg-surface-100 flex-shrink-0" />
                        }
                        <p className="font-medium text-surface-900 max-w-[180px] truncate">{p.name}</p>
                      </div>
                    </td>

                    <td>
                      <span className="font-mono text-xs text-surface-400">{p.sku || '—'}</span>
                    </td>

                    <td>
                      <StockEditor product={p} onSaved={refetch} />
                    </td>

                    <td>
                      <span className="font-mono text-sm text-surface-500">
                        {p.inventory?.reserved_qty ?? 0}
                      </span>
                    </td>

                    <td>
                      <span className="font-mono text-sm text-surface-500">
                        {p.inventory?.sold_qty ?? 0}
                      </span>
                    </td>

                    <td>
                      <span className={`font-mono text-xs
                        ${(p.inventory?.stock_qty ?? p.stock_qty ?? 0) <= (p.low_stock_alert || 5)
                          ? 'text-danger-600 font-semibold'
                          : 'text-surface-400'}`}>
                        {p.low_stock_alert || 5}
                      </span>
                    </td>

                    <td>
                      <StatusBadge status={p.is_active ? 'active' : 'suspended'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}